import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAgencyCompany, useProfile } from '@mint-lib/profile-helpers';
import {
  Divider,
  Input,
  InputLabel,
  InputRoot,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type { Label } from '../../api/users/users.types.js';
import ConditionalSkeleton from '../../atoms/ConditionalSkeleton/ConditionalSkeleton.jsx';
import FormBlock from '../../atoms/FormBlock/index.js';
import LabelsHierarchy from '../../molecules/LabelsHierarchy/LabelsHierarchy.jsx';
import SelectField from '../../molecules/SelectField/SelectField.jsx';
import type { OptionType } from '../../types/OptionType.js';
import { UserModalType } from './validation.js';

export type UserView = {
  formInstance: UseFormReturn<UserModalType>;
  isReadonlyPermission: boolean;
  roleOptions: OptionType[];
  clientsOptions: OptionType[] | null;
  assignedClients: number[] | null;
  setAssignedClients: Dispatch<SetStateAction<number[] | null>>;
  isLoading: boolean;
  isSaving: boolean;
  initialTags: Label[] | null;
  isTagsLoading?: boolean;
};

const UserView = ({
  formInstance,
  isReadonlyPermission,
  roleOptions,
  clientsOptions,
  assignedClients,
  setAssignedClients,
  isLoading,
  isSaving,
  initialTags,
  isTagsLoading = false,
}: UserView) => {
  const { t } = useTranslation('@myn/permissions');

  const { profile } = useProfile();

  const isAgencyCompany = useIsAgencyCompany();

  const isOwnUser = formInstance.getValues('id') === profile?.id;

  const handleRoleChange = useCallback(
    (e: OptionType['value']) => {
      formInstance.setValue('role', e as number, {
        shouldDirty: true,
      });
      formInstance.trigger('role');
    },
    [formInstance],
  );

  const handleAssignedClientsChange = useCallback(
    (selected: OptionType['value'][]) => {
      const e = selected.map(Number);
      if (e) {
        setAssignedClients(e);

        formInstance.setValue('assignedClients', e, {
          shouldDirty: true,
        });

        formInstance.trigger('assignedClients');
      }
    },
    [formInstance, setAssignedClients],
  );

  const fullName = useMemo(() => {
    const firstName = formInstance.getValues('firstName') || '';
    const lastName = formInstance.getValues('lastName') || '';
    return `${firstName} ${lastName}`.trim();
  }, [formInstance]);

  return (
    <div className="flex gap-4">
      <div
        className={cn(
          'flex flex-col gap-4',
          isAgencyCompany ? 'w-full' : 'w-1/3',
        )}
      >
        <p className={typographyVariants({ variant: 'h4' })}>
          {t('General information')}
        </p>
        <div className="flex flex-col gap-4">
          <ConditionalSkeleton isLoading={isLoading}>
            <InputRoot>
              <InputLabel label={t('Full name')} htmlFor="fullName" />
              <Input id="fullName" value={fullName} size="small" disabled />
            </InputRoot>
          </ConditionalSkeleton>
          <ConditionalSkeleton isLoading={isLoading}>
            <InputRoot>
              <InputLabel label={t('Email')} htmlFor="email" />
              <Input
                id="email"
                value={formInstance.getValues('email')}
                disabled
                size="small"
              />
            </InputRoot>
          </ConditionalSkeleton>
          <ConditionalSkeleton isLoading={isLoading}>
            <InputRoot>
              <InputLabel label={t('Mobile number')} htmlFor="mobileNumber" />
              <Input
                id="mobileNumber"
                value={formInstance.getValues('mobileNumber') || ''}
                disabled
                size="small"
              />
            </InputRoot>
          </ConditionalSkeleton>
        </div>

        <div>
          <ConditionalSkeleton isLoading={isLoading}>
            <SelectField
              id="role"
              label={t('Role*')}
              placeholder={t('Select role')}
              options={roleOptions}
              value={Number(formInstance.getValues('role')) || ''}
              disabled={isReadonlyPermission || isOwnUser || isSaving}
              infoText={t(`This role will be applied to the agency workspace.`)}
              error={Boolean(formInstance.formState.errors.role?.message)}
              helperText={formInstance.formState.errors.role?.message}
              onChange={handleRoleChange}
              size="small"
            />
          </ConditionalSkeleton>
        </div>
        {isAgencyCompany ? (
          <FormBlock>
            <div className="max-w-122 grow">
              <ConditionalSkeleton height={60} isLoading={isLoading}>
                <SelectField
                  multiple
                  id="assignedClients"
                  label={t('Clients')}
                  placeholder={t('Assign clients')}
                  infoText={t(`Assign clients to this user`)}
                  options={clientsOptions ?? []}
                  value={assignedClients ?? []}
                  disabled={isReadonlyPermission || isSaving}
                  onChange={handleAssignedClientsChange}
                />
              </ConditionalSkeleton>
            </div>
          </FormBlock>
        ) : null}
      </div>
      {isAgencyCompany ? null : (
        <>
          <Divider orientation="vertical" />
          <div className="w-2/3">
            <LabelsHierarchy
              // The panel snapshots initialTags into state on mount, so it has
              // to remount once they arrive or it keeps the empty snapshot.
              key={isTagsLoading ? 'tags-loading' : 'tags-ready'}
              formInstance={formInstance}
              isReadonlyPermission={isReadonlyPermission}
              initialTags={initialTags}
              isSaving={isSaving}
              isTagsLoading={isTagsLoading}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UserView;
